import { FC, KeyboardEvent, useCallback } from "react";

import Box from "@mui/material/Box";

import { useTranslate } from "@/common/hooks/useTranslate";
import { Add as FilledAdd } from "@/icons/filled";

interface AddDividerProps {
  onClick: () => void;
  ariaLabel?: string;
}

export const AddDivider: FC<AddDividerProps> = ({ onClick, ariaLabel }) => {
  const { t } = useTranslate();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel ?? t("dashboard", "liveFormEditor.insertItem")}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-test="add-divider"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        paddingY: theme.spacing(1.5),
        cursor: "pointer",
        outline: "none",
        transition: theme.transitions.create(["gap"], {
          duration: theme.transitions.duration.shorter,
        }),
        "& [data-add-divider-line]": {
          flex: 1,
          minWidth: 0,
          height: "1px",
          borderRadius: "10px",
          backgroundColor: theme.palette.border.neutral.quiet,
          transition: theme.transitions.create(["background-color"], {
            duration: theme.transitions.duration.shorter,
          }),
        },
        "& [data-add-divider-icon]": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 16,
          height: 16,
          fill: theme.palette.border.neutral.quiet,
          transform: "rotate(0deg)",
          transition: theme.transitions.create(["width", "height", "fill", "transform"], {
            duration: theme.transitions.duration.shorter,
          }),
          "& svg": { display: "block", width: "83.33%", height: "83.33%" },
        },
        "&:hover, &:focus-visible": {
          gap: theme.spacing(3),
        },
        "&:hover [data-add-divider-line], &:focus-visible [data-add-divider-line]": {
          backgroundColor: theme.palette.border.brand.intense,
        },
        "&:hover [data-add-divider-icon], &:focus-visible [data-add-divider-icon]": {
          width: 20,
          height: 20,
          fill: theme.palette.surface.brand.intense,
          transform: "rotate(90deg)",
        },
      })}
    >
      <Box data-add-divider-line />
      <Box data-add-divider-icon>
        <FilledAdd />
      </Box>
      <Box data-add-divider-line />
    </Box>
  );
};
